import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Modal } from 'react-bootstrap';
import * as Actions from 'redux/actions/index';
import SyllabusUploadStep1 from './SyllabusUploadStep1';
import SyllabusUploadStep2 from './SyllabusUploadStep2';
import SyllabusUploadStepFinal from './SyllabusUploadStepFinal';
import SyllabusUploadStepPublishArticles from './SyllabusUploadStepPublishArticles';
import SyllabusUploadStepPreferredSources from './SyllabusUploadStepPreferredSources';
import SyllabusUploadStepReadingOptions from './SyllabusUploadStepReadingOptions';
import SyllabusUploadStepStudentEmails from './SyllabusUploadStepStudentEmails';
import SyllabusUploadStepPreferenceSuccess from './SyllabusUploadStepPreferenceSuccess';
import SyllabusUploadStepCourseCreated from './SyllabusUploadStepCourseCreated';
import SyllabusUploadStepNewsSuggestions from './SyllabusUploadStepNewsSuggestions';
import './SyllabusUpload.scss';
import { Icon, ICONS } from 'components/Icons';
import ActionLink from 'components/elements/molecules/ActionLink/ActionLink';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import ConfirmModal from 'components/modals/Confirm/ConfirmModal';
import { DEFAULT_SOURCE_IDS } from './constants';

// function formatCountdown(seconds) {
//   const s = Math.max(0, Math.round(seconds));
//   return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
// }

function SyllabusAnalysisIndicator({
  uploadUiState,
  uploadProgress,
  onReview,
}) {
  const { estimatedSecondsRemaining, currentStep, progressPercent } =
    uploadProgress;
  const isActive =
    uploadUiState === 'sending' ||
    uploadUiState === 'analyzing' ||
    uploadUiState === 'retrying';
  const isSuccess = uploadUiState === 'success';

  if (!isActive && !isSuccess) return null;

  const title =
    uploadUiState === 'sending'
      ? 'Uploading your file...'
      : 'Analyzing your syllabus';

  return (
    <div
      className={`syllabus-analysis-indicator${isSuccess ? ' is-success' : ''}`}
    >
      <div className="syllabus-analysis-indicator-body">
        {isActive && (
          <svg
            className="syllabus-analysis-spinner"
            width="28"
            height="28"
            viewBox="0 0 32 32"
          >
            <circle
              cx="16"
              cy="16"
              r="12"
              fill="none"
              stroke="#e8eaed"
              strokeWidth="3"
            />
            <circle
              cx="16"
              cy="16"
              r="12"
              fill="none"
              stroke="#1286c8"
              strokeWidth="3"
              strokeDasharray="19 57"
              strokeLinecap="round"
            />
          </svg>
        )}
        {isSuccess && (
          <div className="syllabus-analysis-check-icon">
            <Icon icon={ICONS.VERIFIED} />
          </div>
        )}
        <div className="syllabus-analysis-copy">
          <div className="syllabus-analysis-copy-top">
            <div className="syllabus-analysis-title">
              {isSuccess ? 'Syllabus ready' : title}
            </div>
            {isSuccess && onReview && (
              <ActionLink
                onClick={onReview}
                className="syllabus-analysis-review-link"
              >
                Review
              </ActionLink>
            )}
          </div>
          {currentStep && isActive && (
            <div className="syllabus-analysis-sub">{currentStep}</div>
          )}
        </div>
      </div>
      {isActive && (
        <div className="syllabus-analysis-progress">
          <div
            className={`syllabus-analysis-progress-fill${progressPercent > 0 ? '' : ' is-indeterminate'}`}
            style={
              progressPercent > 0 ? { width: `${progressPercent}%` } : undefined
            }
          />
        </div>
      )}
    </div>
  );
}

const DEFAULT_DELIVERY_PREFERENCES = {
  deliveryDay: 'Sunday',
  reminderIfNotReviewed: true,
  showInstructorNotes: false,
  autoPublishWithoutReview: false,
  courseName: '',
  courseDescription: '',
  sources: DEFAULT_SOURCE_IDS,
  readingOptions: ['readings_in_wild'],
  sendStudentsSuggestedReadingsEmail: true,
  newsSuggestionsEnabled: false,
};

const SUCCESS_STEP_DELAY_MS = 2000;

const INITIAL_UPLOAD_PROGRESS = {
  eventName: '',
  currentStep: '',
  progressPercent: 0,
  estimatedSecondsRemaining: 0,
  isRetryAnalysis: false,
};

const SENDING_EVENTS = new Set([
  'received_file',
  'uploading_to_storage',
  'uploaded_to_storage',
  'uploading_to_openai',
  'uploaded_to_openai',
]);

const SUCCESS_EVENTS = new Set(['analysis_completed', 'saving_results']);

const getUploadUiStateFromProgress = ({
  eventName = '',
  currentStep = '',
  isRetryAnalysis = false,
}) => {
  if (SUCCESS_EVENTS.has(eventName)) {
    return 'success';
  }

  if (eventName === 'analyzing_file') {
    return isRetryAnalysis ? 'retrying' : 'analyzing';
  }

  if (SENDING_EVENTS.has(eventName)) {
    return 'sending';
  }

  const normalizedStep = String(currentStep).trim().toLowerCase();

  if (!normalizedStep) {
    return 'idle';
  }

  if (
    normalizedStep.includes('saving syllabus rows') ||
    normalizedStep.includes('file analysis completed')
  ) {
    return 'success';
  }

  if (
    normalizedStep.includes('re-analyzing syllabus file') ||
    normalizedStep.includes('analyzing your file again')
  ) {
    return 'retrying';
  }

  if (
    normalizedStep.includes('analyzing syllabus file') ||
    normalizedStep.includes('analyzing your file')
  ) {
    return 'analyzing';
  }

  if (
    normalizedStep.includes('file received') ||
    normalizedStep.includes('uploading file to storage') ||
    normalizedStep.includes('file uploaded to storage') ||
    normalizedStep.includes('uploading file for analysis') ||
    normalizedStep.includes('file uploaded for analysis') ||
    normalizedStep.includes('sending your file to') ||
    normalizedStep.includes('starting upload')
  ) {
    return 'sending';
  }

  return 'idle';
};

const toTitleCase = (value = '') =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';

const getResolvedSources = (sources) =>
  Array.isArray(sources) && sources.length > 0
    ? sources
    : DEFAULT_DELIVERY_PREFERENCES.sources;

const normalizeSyllabusRows = (data) => {
  const existingRows = data?.rows || data?.syllabus?.rows || [];

  return existingRows.map((row) => ({
    _id: row._id,
    date: row.date,
    weekStartDate: row.weekStartDate,
    skipWeek: row.skipWeek === true,
    topic: row.name || row.topic || '',
    name: row.name || row.topic || '',
    description: row.description || '',
    invalidLectureTopic: row.invalidLectureTopic === true,
    invalidLectureTopicReason:
      row.invalidLectureTopicReason || row.invalidTopicReason || '',
    invalidLectureDescription: row.invalidLectureDescription === true,
    invalidLectureDescriptionReason: row.invalidLectureDescriptionReason || '',
    courseTopicId: row.courseTopicId || null,
    dueDate: row.dueDate || null,
  }));
};

const normalizeDeliveryPreferences = (
  data,
  courseName = '',
  courseDescription = '',
) => {
  const syllabus = data?.syllabus || data || {};
  const settings = syllabus?.settings || data?.settings || {};
  const deliveryDay = settings.deliveryDay || syllabus.deliveryDay;

  return {
    deliveryDay: toTitleCase(
      deliveryDay || DEFAULT_DELIVERY_PREFERENCES.deliveryDay,
    ),
    reminderIfNotReviewed:
      settings.reminderIfNotReviewedByTuesday ??
      syllabus.reminderIfNotReviewedByTuesday ??
      DEFAULT_DELIVERY_PREFERENCES.reminderIfNotReviewed,
    showInstructorNotes:
      settings.showInstructorNotesToStudents ??
      syllabus.showInstructorNotesToStudents ??
      DEFAULT_DELIVERY_PREFERENCES.showInstructorNotes,
    autoPublishWithoutReview:
      settings.autoPublishWithoutReview ??
      syllabus.autoPublishWithoutReview ??
      DEFAULT_DELIVERY_PREFERENCES.autoPublishWithoutReview,
    courseName: settings.courseName || courseName || '',
    courseDescription: settings.courseDescription || courseDescription || '',
    sources: getResolvedSources(settings.sources),
    readingOptions:
      Array.isArray(settings.readingOptions) && settings.readingOptions.length
        ? settings.readingOptions
        : DEFAULT_DELIVERY_PREFERENCES.readingOptions,
    sendStudentsSuggestedReadingsEmail:
      settings.sendStudentsSuggestedReadingsEmail ??
      DEFAULT_DELIVERY_PREFERENCES.sendStudentsSuggestedReadingsEmail,
    // newsSuggestionSettings.enabled is the single on/off switch for news
    // suggestions; must be explicitly true - undefined counts as off.
    newsSuggestionsEnabled: syllabus.enabled === true,
  };
};

function SyllabusUploadModal({
  courseId,
  courses,
  onClose,
  actions,
  mode = 'add', // 'add', 'edit', 'preference', 'manage_lecture_schedule', 'sources', or 'news_suggestions'
  onSyllabusSaved, // Callback when syllabus is saved
  skipTo, // NEW: step name or number to skip to
  openReviewReadingsModal,
}) {
  // Map skipTo string to step number
  const getStepFromSkipTo = useCallback(
    (skipToValue) => {
      if (!skipToValue) {
        if (mode === 'edit') return 2;
        if (mode === 'preference') return 10;
        if (mode === 'manage_lecture_schedule') return 2;
        if (mode === 'sources') return 6;
        if (mode === 'news_suggestions') return 18;
        return 1;
      }
      if (typeof skipToValue === 'number') return skipToValue;
      // Map string values to step numbers
      switch (skipToValue) {
        case 'upload':
          return 1;
        case 'manual':
          return 2;
        case 'delivery':
          return 3;
        case 'review':
          return 4;
        default:
          if (mode === 'edit') return 2;
          if (mode === 'preference') return 10;
          if (mode === 'manage_lecture_schedule') return 2;
          return 1;
      }
    },
    [mode],
  );

  const [step, setStep] = useState(getStepFromSkipTo(skipTo));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(INITIAL_UPLOAD_PROGRESS);
  const [uploadError, setUploadError] = useState('');
  const [deliveryPreferences, setDeliveryPreferences] = useState(
    DEFAULT_DELIVERY_PREFERENCES,
  );
  const [savedSyllabus, setSavedSyllabus] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(true);
  const successDelayRef = useRef(null);
  const successDelayPromiseRef = useRef(null);
  const stepRef = useRef(step);
  const deliveryPreferencesRef = useRef(deliveryPreferences);
  const isPreferencesSetRef = useRef(false);
  const initialNewsPrefsRef = useRef(null);
  const isScheduledConfirmedRef = useRef(false);
  const [isScheduledConfirmed, setIsScheduledConfirmed] = useState(false);
  const [isFileUploadFlow, setIsFileUploadFlow] = useState(false);
  const [isPreferencesSet, setIsPreferencesSet] = useState(false);
  const [step2HasUnsavedChanges, setStep2HasUnsavedChanges] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [loadingSyllabus, setLoadingSyllabus] = useState(!!courseId);
  const isEditMode = mode === 'edit';
  const isManageLectureScheduleMode = mode === 'manage_lecture_schedule';
  const isSourcesMode = mode === 'sources';
  const isNewsSuggestionsMode = mode === 'news_suggestions';

  const course = useMemo(
    () =>
      (courses || []).find(
        (c) => c.courseId === courseId || c._id === courseId,
      ),
    [courses, courseId],
  );

  const blankRow = () => ({
    _tempId: `_temp_${Date.now()}`,
    date: '',
    topic: '',
    name: '',
    description: '',
    checkValidity: false,
  });

  const loadExistingSyllabus = useCallback(async () => {
    setLoadingSyllabus(true);
    try {
      const data = await actions.getSyllabus(courseId);
      const normalizedRows = normalizeSyllabusRows(data);

      setRows(normalizedRows.length > 0 ? normalizedRows : [blankRow()]);

      const normalized = normalizeDeliveryPreferences(
        data,
        course?.name || '',
        course?.description || '',
      );
      setDeliveryPreferences(normalized);
      initialNewsPrefsRef.current = {
        newsSuggestionsEnabled: normalized.newsSuggestionsEnabled,
        autoPublishWithoutReview: normalized.autoPublishWithoutReview,
        sendStudentsSuggestedReadingsEmail: normalized.sendStudentsSuggestedReadingsEmail,
      };
      setSavedSyllabus(data);
    } catch (error) {
      console.error('Error loading existing syllabus:', error);
    } finally {
      setLoadingSyllabus(false);
    }
  }, [actions, courseId, course]);

  const clearSuccessDelay = useCallback(() => {
    if (successDelayRef.current) {
      clearTimeout(successDelayRef.current);
      successDelayRef.current = null;
    }

    successDelayPromiseRef.current = null;
  }, []);

  const startSuccessDelay = useCallback(() => {
    if (successDelayPromiseRef.current) {
      return successDelayPromiseRef.current;
    }

    successDelayPromiseRef.current = new Promise((resolve) => {
      if (successDelayRef.current) {
        clearTimeout(successDelayRef.current);
      }

      successDelayRef.current = setTimeout(() => {
        successDelayRef.current = null;
        successDelayPromiseRef.current = null;
        resolve();
      }, SUCCESS_STEP_DELAY_MS);
    });

    return successDelayPromiseRef.current;
  }, []);

  const resetUploadState = useCallback(() => {
    clearSuccessDelay();
    setLoading(false);
    setUploadError('');
    setUploadProgress(INITIAL_UPLOAD_PROGRESS);
  }, [clearSuccessDelay]);

  // Reset state when modal is opened
  useEffect(
    () => {
      setStep(getStepFromSkipTo(skipTo));
      setRows([]);
      setLoading(false);
      setUploadError('');
      setUploadProgress(INITIAL_UPLOAD_PROGRESS);
      setDeliveryPreferences({
        ...DEFAULT_DELIVERY_PREFERENCES,
        courseName: course?.name || '',
        courseDescription: course?.description || '',
      });
      setSavedSyllabus(null);

      if (courseId) {
        loadExistingSyllabus();
      }
      clearSuccessDelay();
    },
    [
      // courseId,
      // isEditMode,
      // loadExistingSyllabus,
      // course,
      // clearSuccessDelay,
      // skipTo,
      // getStepFromSkipTo,
    ],
  );

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    deliveryPreferencesRef.current = deliveryPreferences;
  }, [deliveryPreferences]);

  useEffect(() => {
    isPreferencesSetRef.current = isPreferencesSet;
  }, [isPreferencesSet]);

  useEffect(() => {
    isScheduledConfirmedRef.current = isScheduledConfirmed;
  }, [isScheduledConfirmed]);

  useEffect(() => () => clearSuccessDelay(), [clearSuccessDelay]);

  const uploadUiState = useMemo(() => {
    if (uploadError) {
      return 'error';
    }

    const derivedState = getUploadUiStateFromProgress(uploadProgress);
    if (derivedState !== 'idle') {
      return derivedState;
    }

    return 'idle';
  }, [uploadError, uploadProgress]);

  const showWizardDots =
    !(step === 1 && uploadUiState !== 'idle') &&
    step !== 10 &&
    step !== 11 &&
    step !== 17 &&
    step !== 18 &&
    !isManageLectureScheduleMode &&
    mode !== 'preference' &&
    !isSourcesMode &&
    !isNewsSuggestionsMode;

  const wizardTotalDots = isFileUploadFlow ? 3 : 2;
  const wizardPosition = isFileUploadFlow
    ? step <= 1
      ? 1
      : step <= 2
        ? 2
        : 3
    : step <= 1
      ? 1
      : 2;

  const handleAnalyze = async (file, guidance) => {
    setIsFileUploadFlow(true);
    setLoading(true);
    setUploadError('');
    clearSuccessDelay();
    setUploadProgress({
      ...INITIAL_UPLOAD_PROGRESS,
      currentStep: 'Sending your file to servers',
    });

    let successDelayPromise = null;
    const ensureSuccessDelay = (progressLike) => {
      if (getUploadUiStateFromProgress(progressLike) === 'success') {
        successDelayPromise = successDelayPromise || startSuccessDelay();
      }
    };

    try {
      const response = await actions.uploadSyllabus(courseId, file, {
        guidance,
        onProgress: (progressUpdate) => {
          ensureSuccessDelay(progressUpdate);

          setUploadProgress((previous) => ({
            eventName: progressUpdate?.eventName || previous.eventName || '',
            currentStep:
              progressUpdate?.currentStep || previous.currentStep || '',
            progressPercent:
              typeof progressUpdate?.progressPercent === 'number'
                ? progressUpdate.progressPercent
                : previous.progressPercent,
            estimatedSecondsRemaining:
              typeof progressUpdate?.estimatedSecondsRemaining === 'number'
                ? progressUpdate.estimatedSecondsRemaining
                : previous.estimatedSecondsRemaining,
            isRetryAnalysis:
              typeof progressUpdate?.isRetryAnalysis === 'boolean'
                ? progressUpdate.isRetryAnalysis
                : previous.isRetryAnalysis,
          }));

          const currentStep = progressUpdate?.currentStep;
          console.log(currentStep, progressUpdate);
        },
      });

      const mappedRows = (response.rows || []).map((row) => ({
        _id: row._id,
        date: row.date,
        weekStartDate: row.weekStartDate,
        skipWeek: row.skipWeek === true,
        topic: row.name || row.topic || '',
        name: row.name || row.topic || '',
        description: row.description || '',
        invalidLectureTopic: row.invalidLectureTopic === true,
        invalidLectureTopicReason:
          row.invalidLectureTopicReason || row.invalidTopicReason || '',
        invalidLectureDescription: row.invalidLectureDescription === true,
        invalidLectureDescriptionReason:
          row.invalidLectureDescriptionReason || '',
      }));
      setRows(mappedRows);

      savedSyllabus.syllabus = {
        ...savedSyllabus.syllabus,
        originalFilename: response.syllabus.originalFilename,
      };

      setSavedSyllabus(savedSyllabus);

      if (stepRef.current === 1 || stepRef.current === 12) {
        setIsModalVisible(true);
        setUploadProgress(INITIAL_UPLOAD_PROGRESS);

        // if (!isScheduledConfirmedRef.current) {
        //   if (
        //     (deliveryPreferencesRef.current.readingOptions || []).includes(
        //       'readings_in_wild_course_topics',
        //     )
        //   ) {
        //     setStep(14);
        //   } else {
        //     setStep(2);
        //   }
        // } else if (!isPreferencesSetRef.current) {
        //   setStep(3);
        // } else if (
        //   (deliveryPreferencesRef.current.readingOptions || []).includes(
        //     'readings_in_wild_course_topics',
        //   )
        // ) {
        //   setStep(14);
        // } else {
        //   setStep(13);
        // }
        setStep(2);
      }
    } catch (error) {
      console.error('Error analyzing syllabus', error);
      setUploadError(
        error?.message || 'Failed to upload syllabus. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 1 skip: go directly to step 2 with empty table
  const handleSkip = () => {
    setRows([blankRow()]);
    setStep(2);
    setLoading(false);
  };

  const handleConfirmSchedule = async ({ savedCount = 0 } = {}) => {
    try {
      setLoading(true);

      // const normalizedRows = rows.map((row) => ({
      //   date: row.date,
      //   name: (row.topic || row.name || '').trim(),
      //   description: row.description || '',
      //   ...(row._id ? { _id: row._id } : {}),
      // }));

      // const hasUnsavedRows = normalizedRows.some((row) => !row._id);

      // if (hasUnsavedRows) {
      //   await actions.createDraftSyllabus(courseId, normalizedRows);
      //   const latestSyllabus = await actions.getSyllabus(courseId);
      //   const refreshedRows = normalizeSyllabusRows(latestSyllabus);
      //   setRows(refreshedRows);
      // }

      // let latestSyllabus = await actions.updateSyllabusStatus(
      //   courseId,
      //   'published',
      // );

      // if (
      //   !latestSyllabus?.generatedDetails &&
      //   !latestSyllabus?.syllabus?.generatedDetails
      // ) {
      let latestSyllabus = await actions.getSyllabus(courseId);
      // }

      setSavedSyllabus(latestSyllabus);

      if (onSyllabusSaved) {
        onSyllabusSaved({ savedCount: savedCount });
      }

      actions.getCourseTopics(courseId);

      if (isManageLectureScheduleMode) {
        onClose();
        return;
      }

      // setStep(
      //   isPreferencesSet && isFileUploadFlow ? 15 : isPreferencesSet ? 9 : 3,
      // );
      setStep(17);
      setIsScheduledConfirmed(true);
    } catch (error) {
      console.error('Error confirming schedule', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeliveryPreferences = async (overrides = {}) => {
    const prefs = { ...deliveryPreferences, ...overrides };
    try {
      setLoading(true);
      const settingsPayload = {
        deliveryDay: (prefs.deliveryDay || 'Sunday').toLowerCase(),
        reminderIfNotReviewedByTuesday: prefs.reminderIfNotReviewed,
        showInstructorNotesToStudents: prefs.showInstructorNotes,
        autoPublishWithoutReview: prefs.autoPublishWithoutReview,
        courseName: prefs.courseName || '',
        courseDescription: prefs.courseDescription || '',
        sources: getResolvedSources(prefs.sources),
        readingOptions:
          prefs.readingOptions || DEFAULT_DELIVERY_PREFERENCES.readingOptions,
        sendStudentsSuggestedReadingsEmail:
          prefs.sendStudentsSuggestedReadingsEmail ??
          DEFAULT_DELIVERY_PREFERENCES.sendStudentsSuggestedReadingsEmail,
      };
      const newsSuggestionsEnabled =
        prefs.newsSuggestionsEnabled ??
        DEFAULT_DELIVERY_PREFERENCES.newsSuggestionsEnabled;
      await Promise.all([
        actions.updateSyllabusSettings(courseId, settingsPayload),
        actions.updateSyllabusStatus(courseId, newsSuggestionsEnabled),
      ]);
      setIsPreferencesSet(true);

      if (isSourcesMode || isNewsSuggestionsMode) {
        onClose();
      } else if (mode === 'preference') {
        setStep(11);
      } else if (isFileUploadFlow) {
        setStep(isScheduledConfirmed ? 15 : 12);
      } else {
        setStep(mode === 'add' ? 16 : 9);
      }
    } catch (error) {
      console.error('Error confirming syllabus', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onClose();

    actions.getCourseTopics(courseId); // Refresh topics in case they were updated based on syllabus
  };

  const handleMinimize = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <Modal
        show={isModalVisible}
        className={`syllabus-upload-modal${[3, 10, 12, 14, 17].includes(step) ? ' syllabus-upload-modal--narrow' : ''}`}
        dialogClassName="syllabus-upload-dialog"
        enforceFocus={false}
      >
        <Modal.Body>
          {showCloseConfirm && (
            <ConfirmModal
              model={{
                message:
                  'You have unsaved changes. Are you sure you want to close?',
              }}
              onClose={(confirmed) => {
                setShowCloseConfirm(false);
                if (confirmed) onClose();
              }}
            />
          )}
          <button
            type="button"
            onClick={() => {
              if (step === 2 && step2HasUnsavedChanges) {
                setShowCloseConfirm(true);
              } else {
                onClose();
              }
            }}
            className="btn-av-container position-absolute mr-3 mt-4 close"
            aria-label="Close"
          >
            <Icon icon={ICONS.CLOSE} />
          </button>
          <div className="main-padding">
            <div className="syllabus-step-progress">
              {showWizardDots && (
                <div className="step-dots">
                  {Array.from({ length: wizardTotalDots }, (_, i) => i + 1).map(
                    (s) => (
                      <div
                        key={s}
                        className={`dot ${wizardPosition > s ? 'done' : ''} ${wizardPosition === s ? 'active' : ''}`}
                      />
                    ),
                  )}
                </div>
              )}
              {mode === 'preference' && [6, 7, 8].includes(step) && (
                <div className="preference-steps-header">
                  <div className="preference-steps-bar">
                    {['Sources', 'Teacher Review', 'Delivery'].map(
                      (label, i) => {
                        const stepNum = i + 6;
                        return (
                          <div
                            key={label}
                            className={`preference-step-item${step > stepNum ? ' done' : ''}${step === stepNum ? ' active' : ''}`}
                          >
                            <div className="preference-step-circle">
                              {step > stepNum ? '✓' : i + 1}
                            </div>
                            <div className="preference-step-label">{label}</div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}
            </div>
            {step === 1 && (
              <SyllabusUploadStep1
                onAnalyze={handleAnalyze}
                onSkip={handleSkip}
                onContinue={handleMinimize}
                onCancel={onClose}
                loading={loading}
                uploadProgress={uploadProgress}
                uploadUiState={uploadUiState}
                uploadError={uploadError}
                onResetUpload={resetUploadState}
                initialScreen={skipTo === 'upload' ? 'upload' : 'decision'}
              />
            )}
            {step === 2 && (
              <SyllabusUploadStep2
                rows={rows}
                onChange={setRows}
                onBack={
                  isEditMode || isManageLectureScheduleMode
                    ? onClose
                    : () => {
                        setLoading(false);
                        setUploadProgress(INITIAL_UPLOAD_PROGRESS);
                        setStep(1);
                      }
                }
                onConfirm={handleConfirmSchedule}
                loading={loading}
                courseId={courseId}
                deliveryDay={deliveryPreferences.deliveryDay}
                backLabel={
                  isEditMode || isManageLectureScheduleMode ? 'Cancel' : 'Back'
                }
                syllabusData={savedSyllabus?.syllabus || {}}
                isFileUploadFlow={isFileUploadFlow}
                openReviewReadingsModal={openReviewReadingsModal}
                actions={actions}
                isManageLectureScheduleMode={isManageLectureScheduleMode}
                onHasUnsavedChanges={setStep2HasUnsavedChanges}
                onUpload={() => {
                  setLoading(false);
                  setUploadProgress(INITIAL_UPLOAD_PROGRESS);
                  setStep(1);
                }}
              />
            )}
            {step === 3 && (
              <div className="syllabus-step">
                <div className="main-con">
                  <div className="modal-header">
                    <div className="syllabus-step-title">
                      {`You've set up your schedule with ${rows.length} topic${rows.length !== 1 ? 's' : ''}.`}
                    </div>
                    <p className="syllabus-step-desc mt-1">
                      Now, you can set your student reading preferences.
                    </p>
                  </div>
                  <div className="syllabus-step-footer">
                    <div />
                    <RectangularButton darkButton onClick={() => setStep(5)}>
                      Continue
                    </RectangularButton>
                  </div>
                </div>
              </div>
            )}
            {/* {step === 3 && (
            <SyllabusUploadStepCourseName
              courseName={deliveryPreferences.courseName}
              onBack={() => setStep(2)}
              onConfirm={(courseName) => {
                setDeliveryPreferences((prev) => ({ ...prev, courseName }));
                setStep(4);
              }}
              loading={false}
            />
          )}
          {step === 4 && (
            <SyllabusUploadStepCourseDescription
              courseDescription={deliveryPreferences.courseDescription}
              onBack={() => setStep(3)}
              onConfirm={(courseDescription) => {
                setDeliveryPreferences((prev) => ({
                  ...prev,
                  courseDesstcription,
                }));
                setStep(5);
              }}
              loading={false}
            />
          )} */}
            {step === 5 && (
              <SyllabusUploadStepReadingOptions
                value={deliveryPreferences.readingOptions}
                onChange={(readingOptions) =>
                  setDeliveryPreferences((prev) => ({
                    ...prev,
                    readingOptions,
                  }))
                }
                onBack={() => setStep(4)}
                onConfirm={() => {
                  if (
                    (deliveryPreferences.readingOptions || []).includes(
                      'readings_in_wild_course_topics',
                    )
                  ) {
                    setStep(6);
                  } else if (isFileUploadFlow) {
                    setIsPreferencesSet(true);
                    setStep(isScheduledConfirmed ? 13 : 12);
                  } else {
                    setStep(mode === 'add' ? 16 : 11);
                  }
                }}
                loading={false}
              />
            )}
            {step === 6 && (
              <SyllabusUploadStepPreferredSources
                value={deliveryPreferences}
                onChange={setDeliveryPreferences}
                onBack={isSourcesMode ? onClose : () => setStep(5)}
                onConfirm={
                  isSourcesMode
                    ? handleConfirmDeliveryPreferences
                    : () => setStep(7)
                }
                loading={false}
                courseId={courseId}
                mode={mode}
              />
            )}
            {step === 7 && (
              <SyllabusUploadStepPublishArticles
                value={deliveryPreferences}
                onChange={setDeliveryPreferences}
                onBack={() => setStep(6)}
                onConfirm={() => setStep(8)}
                loading={false}
              />
            )}
            {step === 8 && (
              <SyllabusUploadStepStudentEmails
                value={deliveryPreferences}
                onChange={setDeliveryPreferences}
                onBack={() => setStep(7)}
                onConfirm={handleConfirmDeliveryPreferences}
                loading={false}
              />
            )}
            {step === 9 && (
              <SyllabusUploadStepFinal
                rows={rows}
                course={course}
                deliveryPreferences={deliveryPreferences}
                syllabusData={savedSyllabus}
                onDone={handleDone}
                onMinimize={handleMinimize}
                onAdjustSettings={() => setStep(2)}
                isScheduledConfirmed={isScheduledConfirmed}
              />
            )}
            {step === 10 && (
              <div className="syllabus-step">
                <div className="main-con">
                  <div className="modal-header">
                    <div className="syllabus-step-title">
                      Preferences for Weekly News Suggestions
                    </div>
                    <p className="syllabus-step-desc mt-1">
                      We can send you suggested news articles for your course
                      topics.
                    </p>
                  </div>
                  <div className="section-con">
                    Let’s set up your preferences.
                  </div>
                  <div className="syllabus-step-footer">
                    <div />
                    <RectangularButton darkButton onClick={() => setStep(6)}>
                      Continue
                    </RectangularButton>
                  </div>
                </div>
              </div>
            )}
            {step === 11 && (
              <SyllabusUploadStepPreferenceSuccess
                deliveryPreferences={deliveryPreferences}
                course={course}
                rows={rows}
                onDone={handleDone}
              />
            )}
            {step === 12 && (
              <div className="syllabus-step">
                <div className="main-con">
                  <div className="modal-header">
                    <div className="syllabus-step-title">Sounds good.</div>
                    <p className="syllabus-step-desc mt-1">
                      {(deliveryPreferences.readingOptions || []).includes(
                        'readings_in_wild_course_topics',
                      )
                        ? "You've set up your news suggestions. We're still finding your course topics and dates from your file."
                        : "We're still finding your topics and dates from your file."}
                    </p>
                  </div>
                  <div className="syllabus-step-footer">
                    <div />
                    <RectangularButton darkButton onClick={handleMinimize}>
                      Minimize
                    </RectangularButton>
                  </div>
                </div>
              </div>
            )}
            {step === 13 && (
              <div className="syllabus-step">
                <div className="main-con">
                  <div className="modal-header">
                    <div className="syllabus-step-title">
                      {`We finished processing your file, and found ${rows.length} topic${rows.length !== 1 ? 's' : ''}.`}
                    </div>
                  </div>
                  <div className="section-con">
                    <div className="post-create-subtitle">You can now</div>
                    <div className="post-create-checklist">
                      {[
                        'Invite students',
                        'Set up grading rules',
                        'Assign specific articles',
                        'Set up news suggestions',
                      ].map((item) => (
                        <div key={item} className="post-create-checklist-item">
                          <span className="post-create-check-icon">
                            <Icon icon={ICONS.VERIFIED} />
                          </span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="syllabus-step-footer">
                    <div />
                    <RectangularButton darkButton onClick={handleDone}>
                      Done
                    </RectangularButton>
                  </div>
                </div>
              </div>
            )}
            {step === 14 && (
              <div className="syllabus-step">
                <div className="main-con">
                  <div className="modal-header">
                    <div className="syllabus-step-title">
                      {`We completed processing your file and found ${rows.length} lecture topic${rows.length !== 1 ? 's' : ''}.`}
                    </div>
                  </div>
                  <div className="syllabus-step-footer">
                    <div />
                    <RectangularButton darkButton onClick={() => setStep(2)}>
                      Review
                    </RectangularButton>
                  </div>
                </div>
              </div>
            )}
            {step === 15 && (
              <div className="syllabus-step">
                <div className="main-con">
                  <div className="modal-header">
                    <div className="syllabus-step-title">
                      {`You created the course ${course?.name || ''} with ${rows.length} topic${rows.length !== 1 ? 's' : ''}.`}
                    </div>
                  </div>
                  <div className="section-con">
                    <div className="post-create-checklist">
                      {[
                        `You will receive news suggestions every ${toTitleCase(deliveryPreferences.deliveryDay) || 'Sunday'}.`,
                        'You can click into each topic to assign readings, research, and writing prompts.',
                      ].map((item) => (
                        <div key={item} className="post-create-checklist-item">
                          <span className="post-create-check-icon">
                            <Icon icon={ICONS.VERIFIED} />
                          </span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="syllabus-step-footer">
                    <div />
                    <RectangularButton darkButton onClick={handleDone}>
                      Done
                    </RectangularButton>
                  </div>
                </div>
              </div>
            )}
            {step === 16 && (
              <SyllabusUploadStepCourseCreated
                deliveryPreferences={deliveryPreferences}
                course={course}
                rows={rows}
                onDone={handleDone}
              />
            )}
            {step === 17 && (
              <div className="syllabus-step">
                <div className="main-con">
                  <div className="section-con">
                    <div className="syllabus-step-title">
                      {`You created ${rows.length} lecture topic${rows.length !== 1 ? 's' : ''}.`}
                    </div>
                  </div>
                  <div className="syllabus-step-footer">
                    <div />
                    <RectangularButton darkButton onClick={handleDone}>
                      Done
                    </RectangularButton>
                  </div>
                </div>
              </div>
            )}
            {step === 18 && (
              <SyllabusUploadStepNewsSuggestions
                value={deliveryPreferences}
                onChange={setDeliveryPreferences}
                onCancel={onClose}
                onSave={handleConfirmDeliveryPreferences}
                loading={loading}
                loadingSyllabus={loadingSyllabus}
                saveDisabled={
                  loading ||
                  (isNewsSuggestionsMode &&
                    initialNewsPrefsRef.current !== null &&
                    deliveryPreferences.newsSuggestionsEnabled === initialNewsPrefsRef.current.newsSuggestionsEnabled &&
                    deliveryPreferences.autoPublishWithoutReview === initialNewsPrefsRef.current.autoPublishWithoutReview &&
                    deliveryPreferences.sendStudentsSuggestedReadingsEmail === initialNewsPrefsRef.current.sendStudentsSuggestedReadingsEmail)
                }
              />
            )}
          </div>
        </Modal.Body>
      </Modal>
      <SyllabusAnalysisIndicator
        uploadUiState={uploadUiState}
        uploadProgress={uploadProgress}
        onReview={() => {
          setIsModalVisible(true);
          setUploadProgress(INITIAL_UPLOAD_PROGRESS);
          if (isFileUploadFlow) {
            if (
              (deliveryPreferences.readingOptions || []).includes(
                'readings_in_wild_course_topics',
              )
            ) {
              setStep(14);
            } else {
              setStep(13);
            }
          } else {
            setStep(2);
          }
        }}
      />
    </>
  );
}

function mapStateToProps(state) {
  return {
    courses: state.classRoom.courses || [],
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
    openReviewReadingsModal: (topicId) => {
      dispatch(
        Actions.openModal('review-this-week-suggested-readings', { topicId }),
      );
    },
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SyllabusUploadModal);
